import { Switch } from 'antd';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationContext } from '@client/context/NotificationContext';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useMarketplaceQueries } from '@client/queries/useMarketplaceQueries';

type TFeatureFeatureToggleProps = {
  id: string;
  featured: boolean;
};

/**
 * Step 5: admin-only switch toggling the `featured` flag on a marketplace
 * user *feature* (not subscription). Mirrors `FeatureToggle` exactly,
 * pointing at the features admin endpoint.
 * @param {TFeatureFeatureToggleProps} props - Props
 * @returns {React.JSX.Element} The rendered switch
 */
export const FeatureFeatureToggle: React.FC<TFeatureFeatureToggleProps> = ({
  id,
  featured,
}: TFeatureFeatureToggleProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { ability } = useAuth();
  const { notificationApi } = useContext(NotificationContext);
  const { useSetFeatureFeatured } = useMarketplaceQueries();
  const { mutate, isPending } = useSetFeatureFeatured();

  const [optimistic, setOptimistic] = useState<boolean>(featured);

  React.useEffect(() => {
    setOptimistic(featured);
  }, [featured]);

  const disabled = ability.cannot(
    EAbilityActions.MANAGE,
    EAbilitySubjects.MARKETPLACE,
  );

  const onChange = (next: boolean): void => {
    const previous = optimistic;
    setOptimistic(next);
    mutate(
      { id, featured: next },
      {
        onSuccess: () => {
          notificationApi?.success({
            message: t('admin.marketplace.toggle.success'),
          });
        },
        onError: () => {
          setOptimistic(previous);
          notificationApi?.error({
            message: t('admin.marketplace.toggle.error'),
          });
        },
      },
    );
  };

  return (
    <Switch
      checked={optimistic}
      loading={isPending}
      disabled={disabled}
      onChange={onChange}
    />
  );
};
